<script setup>
import { translateToUI } from '@sensehawk/expression-editor';
import BIFilter from '~/bi/components/filters/bi-filter-list/bi-filter/bi-filter.vue';
import { useBIColumnPicker } from '~/bi/composables/useBIColumPicker';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { getAllOperatorsConfig, getFilterText } from '~/bi/utils/bi-filter-helper.utils.js';

// --------------------------------- Imports -------------------------------- //

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  columns: {
    type: Array,
    default: () => ([]),
  },
  previousStageFields: {
    type: Array,
    default: () => [],
  },
  fields: {
    type: Array,
    default: () => [],
  },
  tables: {
    type: Array,
    required: true,
  },
  existingColumnFilters: {
    type: Array,
    default: () => ([]),
  },
  containerClasses: {
    type: String,
    default: '',
  },
});

// ---------------------------------- Emits --------------------------------- //
const emit = defineEmits(['filterSelected', 'removeFilterField', 'editAppliedFilter', 'reorder']);

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //
const all_operators = getAllOperatorsConfig();
const { constructFieldName, constructColumnAlias } = useBIQueryBuilder();

// ------------------------ Variables - Local - refs ------------------------ //

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //

const all_fields = computed(() => [...props.previousStageFields.map(field => ({ ...field, alias: constructColumnAlias({ table_name: 'Previous stage', column_name: field.alias }), field: constructFieldName({ label: field.alias, table_name: 'results' }), type: field.field_type, is_stage_field: true })), ...props.fields.map(field => ({ ...field, field: field.alias, type: field.field_type, is_stage_field: true }))]);

const column_config_from_field = filter => all_fields.value.find(field => field.field === filter.field);
const column_config_from_table = filter => props.tables.reduce((val, table) => val || table.columns.find(column => column.field === filter.field), null);
const column_config_for_filter = filter => filter.meta?.is_stage_field ? column_config_from_field(filter) : column_config_from_table(filter);
const operator_config_for_filter = filter => all_operators[filter.op];

const existing_column_filters = computed(() => props.existingColumnFilters.map(filter => ({
  expr: filter.expr,
  column_config: filter.expr ? { label: filter.expr, expr: filter.expr, type: 'text', alias: filter.expr, is_stage_field: true } : column_config_for_filter(filter),
  filter_config: filter.expr ? undefined : { ...filter.meta?.filter_config, operator: operator_config_for_filter(filter) },
})));

const columns = computed(() => {
  // If columns is empty and has existing filters, we need to show only those columns with filters in list
  if (props.columns.length === 0 && existing_column_filters.value.length > 0) {
    return existing_column_filters.value;
  }
  else if (props.columns.length && existing_column_filters.value.length === 0) {
    return props.columns.map(column => ({ column_config: column, filter_config: null }));
  }

  // If we have both show both after updating the data with the existing filters
  return props.columns.map((column) => {
    const existing_filter = existing_column_filters.value.find(filter => filter.column_config.uid === column.uid);
    if (existing_filter) {
      column.filter = existing_filter;
    }
    return column;
  });
});

const draggable = defineAsyncComponent(() => import('vuedraggable'));
const filters = ref([...existing_column_filters.value]);
const is_expression_dropdown_open = ref(false);
const selected_expression = ref(null);

watch(() => existing_column_filters.value, (v) => {
  filters.value = v || [];
}, { immediate: true });

function onReorder() {
  emit('reorder', filters.value);
}

function getExpressionLabel(field) {
  try {
    const { columns } = useBIColumnPicker(props, 'expression');

    return translateToUI(field.expr, columns.value);
  }
  catch (e) {
    logger.log(e);
    return field.expr_label;
  }
}
// -------------------------------- Functions ------------------------------- //

// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
// console.log('Filter List Component Loaded::', props, columns.value);
</script>

<template>
  <div :class="[containerClasses]">
    <template v-if="props.columns.length && existing_column_filters.length === 0">
      <div v-for="column in columns" :key="column.column_config.uid" class="group hover:bg-gray-50 cursor-pointer flex justify-between p-2 rounded-lg" @click="$emit('filterSelected', column.column_config)">
        <HawkText :length="30" class="text-gray-600 text-sm" :content="getFilterText(column.column_config, null)" />
      </div>
    </template>
    <template v-else-if="props.columns.length === 0 && existing_column_filters.length > 0">
      <draggable v-model="filters" item-key="column_config.uid" tag="div" handle=".move" :animation="150" @change="onReorder">
        <template #item="{ element: field, index }">
          <div>
            <div v-if="field.expr" class="flex items-center justify-between gap-2 py-2  cursor-pointer group hover:bg-gray-50 rounded-lg" @click="is_expression_dropdown_open = true; selected_expression = field">
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center">
                  <div class="pr-3 move cursor-move invisible group-hover:visible">
                    <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" />
                  </div>
                  <HawkText :length="30" class="text-gray-600 text-sm" :content="getExpressionLabel(field)" />
                </div>
                <div class="text-gray-500 hidden group-hover:block mr-2" @click.stop="emit('removeFilterField', index)">
                  <IconHawkXClose class="size-4" />
                </div>
              </div>
              <bi-filter-expression-editor
                v-if="is_expression_dropdown_open"
                :existing-expression="selected_expression?.expr"
                :fields="fields"
                :previous-stage-fields="previousStageFields"
                :tables="tables"
                @click.stop
                @save="(event$) => { emit('editAppliedFilter', event$, index, () => is_expression_dropdown_open = false) }"
                @close="is_expression_dropdown_open = false"
              />
            </div>
            <HawkMenu
              v-else
              :key="field.column_config.label"
              class="flex items-center justify-between gap-2 py-2  cursor-pointer group hover:bg-gray-50 rounded-lg"
              additional_trigger_classes="!ring-0 !focus:ring-0 !w-full"
              :items="[]"
              position="fixed"
            >
              <template #trigger>
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div class="pr-3 move cursor-move invisible group-hover:visible">
                      <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" />
                    </div>
                    <HawkText :length="30" class="text-gray-600 text-sm" :content="getFilterText(field.column_config, field.filter_config)" />
                  </div>
                  <div class="text-gray-500 hidden group-hover:block mr-2" @click.stop="emit('removeFilterField', index)">
                    <IconHawkXClose class="size-4" />
                  </div>
                </div>
              </template>
              <template #content="{ close: closeOperatorMenu }">
                <div class="bg-white p-1 rounded-lg w-[350px]">
                  <BIFilter :selected-column-config="field.column_config" :filter-config="field.filter_config" @cancel="closeOperatorMenu" @apply="(event$) => { emit('editAppliedFilter', event$, index, closeOperatorMenu) }" />
                </div>
              </template>
            </HawkMenu>
          </div>
        </template>
      </draggable>
    </template>
  </div>
</template>

<style scoped lang="scss">

</style>
